import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import ReactECharts from "echarts-for-react";
import {
  TrendingUp, Star, Users, ArrowLeft, Plus, Target, Award
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const PerformanceReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [reviewsRes, empRes] = await Promise.all([
          axios.get(`${API}/admin/performance-reviews`, { headers }),
          axios.get(`${API}/admin/employees`, { headers })
        ]);
        setReviews(reviewsRes.data.reviews || []);
        setEmployees(empRes.data.employees || []);
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const getRatingColor = (rating) => {
    const colors = {
      1: "text-red-400",
      2: "text-orange-400",
      3: "text-yellow-400",
      4: "text-green-400",
      5: "text-emerald-400"
    };
    return colors[rating] || "text-slate-400";
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: "Needs Improvement",
      2: "Below Expectations",
      3: "Meets Expectations",
      4: "Exceeds Expectations",
      5: "Outstanding"
    };
    return labels[rating] || "N/A";
  };

  const avgRating = reviews.length ? (reviews.reduce((sum, r) => sum + (r.performance_rating || 0), 0) / reviews.length).toFixed(1) : 0;
  const avgGoalCompletion = reviews.length ? (reviews.reduce((sum, r) => sum + ((r.goals_achieved / r.goals_total) * 100 || 0), 0) / reviews.length).toFixed(0) : 0;

  const ratingDistributionOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', backgroundColor: 'rgba(15, 23, 42, 0.95)', borderColor: 'rgba(201, 162, 39, 0.3)', textStyle: { color: '#f8fafc' } },
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      data: [1, 2, 3, 4, 5].map(rating => ({
        value: reviews.filter(r => r.performance_rating === rating).length,
        name: getRatingLabel(rating),
        itemStyle: { color: rating <= 2 ? '#ef4444' : rating === 3 ? '#eab308' : '#22c55e' }
      })).filter(d => d.value > 0),
      label: { color: '#94a3b8' }
    }]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Performance Reviews...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] p-6" data-testid="performance-reviews-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Performance Reviews
            </h1>
            <p className="text-slate-400 text-sm">Track and manage employee performance</p>
          </div>
        </div>
        <Button className="bg-[#c9a227] text-[#0b1c2d] hover:bg-[#c9a227]/90">
          <Plus className="w-4 h-4 mr-2" /> New Review
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass-card">
          <CardContent className="p-4">
            <Users className="w-6 h-6 text-[#c9a227] mb-2" />
            <p className="text-2xl font-bold text-[#c9a227]">{reviews.length}</p>
            <p className="text-xs text-slate-400">Total Reviews</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Star className="w-6 h-6 text-yellow-400 mb-2" />
            <p className="text-2xl font-bold text-yellow-400">{avgRating}</p>
            <p className="text-xs text-slate-400">Avg Rating (1-5)</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Target className="w-6 h-6 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-green-400">{avgGoalCompletion}%</p>
            <p className="text-xs text-slate-400">Avg Goal Completion</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Award className="w-6 h-6 text-purple-400 mb-2" />
            <p className="text-2xl font-bold text-purple-400">{reviews.filter(r => r.performance_rating >= 4).length}</p>
            <p className="text-xs text-slate-400">Top Performers</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Rating Distribution Chart */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Rating Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={ratingDistributionOptions} style={{ height: '200px' }} />
          </CardContent>
        </Card>

        {/* Review Detail Modal */}
        {selectedReview && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <Card className="glass-card w-full max-w-xl">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Performance Review Details</CardTitle>
                <Button variant="ghost" onClick={() => setSelectedReview(null)}>×</Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xl font-bold text-white">{selectedReview.employee_name}</p>
                    <p className="text-slate-400">{selectedReview.review_period}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-3xl font-bold ${getRatingColor(selectedReview.performance_rating)}`}>
                      {selectedReview.performance_rating}/5
                    </p>
                    <p className={`text-sm ${getRatingColor(selectedReview.performance_rating)}`}>
                      {getRatingLabel(selectedReview.performance_rating)}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-slate-400 text-sm mb-2">Goal Achievement</p>
                  <div className="flex items-center gap-3">
                    <Progress 
                      value={(selectedReview.goals_achieved / selectedReview.goals_total) * 100} 
                      className="flex-1 h-3"
                    />
                    <span className="text-white">
                      {selectedReview.goals_achieved}/{selectedReview.goals_total}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-green-400 font-semibold mb-2">Strengths</p>
                    <ul className="space-y-1">
                      {selectedReview.strengths?.map((s, i) => (
                        <li key={i} className="text-slate-300 text-sm flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-yellow-400 font-semibold mb-2">Areas for Improvement</p>
                    <ul className="space-y-1">
                      {selectedReview.improvements?.map((s, i) => (
                        <li key={i} className="text-slate-300 text-sm flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-yellow-400"></span>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div>
                  <p className="text-slate-400 text-sm mb-1">Reviewer Comments</p>
                  <p className="text-white bg-slate-800/50 p-3 rounded-sm">{selectedReview.comments}</p>
                </div>

                <div className="text-sm text-slate-500">
                  Reviewed by: {selectedReview.reviewer_name}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Reviews */}
        <Card className="glass-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-slate-200">Recent Reviews</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reviews.slice(0, 5).map((review) => (
                <div 
                  key={review.id}
                  className="flex items-center justify-between p-3 bg-slate-800/30 rounded-sm hover:bg-slate-800/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedReview(review)}
                >
                  <div>
                    <p className="text-white font-medium">{review.employee_name}</p>
                    <p className="text-slate-500 text-sm">{review.review_period}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 ${i < review.performance_rating ? 'text-[#c9a227] fill-[#c9a227]' : 'text-slate-600'}`} 
                        />
                      ))}
                    </div>
                    <p className="text-slate-400 text-xs mt-1">
                      Goals: {review.goals_achieved}/{review.goals_total}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* All Reviews Table */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">All Performance Reviews</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Period</th>
                  <th>Rating</th>
                  <th>Goals</th>
                  <th>Reviewer</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review) => (
                  <tr key={review.id}>
                    <td className="text-white font-medium">{review.employee_name}</td>
                    <td className="text-slate-300">{review.review_period}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className={`text-lg font-bold ${getRatingColor(review.performance_rating)}`}>
                          {review.performance_rating}
                        </span>
                        <span className="text-slate-500 text-sm">/ 5</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Progress 
                          value={(review.goals_achieved / review.goals_total) * 100} 
                          className="w-16 h-2"
                        />
                        <span className="text-slate-400 text-sm">
                          {review.goals_achieved}/{review.goals_total}
                        </span>
                      </div>
                    </td>
                    <td className="text-slate-300">{review.reviewer_name}</td>
                    <td>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setSelectedReview(review)}
                        className="h-8 px-2 text-[#c9a227]"
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceReviewsPage;
